import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AddOnService } from 'src/app/services/add-on.service';
import { PartnerService } from 'src/app/services/partner.service';
import { ProductService } from 'src/app/services/product.service';

@Component({
  standalone: false,
  selector: 'app-add-ons-detail',
  templateUrl: './add-ons-detail.component.html',
})
export class AddOnsDetailComponent implements OnInit {
  id = '';
  isEditMode = false;
  validationTriggered = false;
  item: any = null;
  form: any = {};
  partners: any[] = [];
  partnersLoaded = false;
  products: any[] = [];
  productsLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private addOnService: AddOnService,
    private partnerService: PartnerService,
    private productService: ProductService,
    private toaster: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.partnerService.list().subscribe({
      next: (partners) => {
        this.partners = [...partners];
        this.partnersLoaded = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.partners = [];
        this.partnersLoaded = true;
        this.toaster.error('Failed to load partners.');
        this.cdr.detectChanges();
      },
    });
    this.productService.list({ page: 1, per_page: 100 }).subscribe({
      next: (result) => {
        this.products = [...(result?.data?.products ?? [])];
        this.productsLoaded = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.products = [];
        this.productsLoaded = true;
        this.toaster.error('Failed to load products.');
        this.cdr.detectChanges();
      },
    });
    this.load();
  }

  load(): void {
    this.addOnService.get(this.id).subscribe({
      next: (result) => {
        this.item = result?.data ?? null;
        this.resetForm();
        this.isEditMode = false;
        this.validationTriggered = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.toaster.error('Add-on not found.');
        this.router.navigate(['/ecomm/add-ons']);
      },
    });
  }

  resetForm(): void {
    this.form = {
      partner_id: this.item.partner_id,
      product_id: this.item.product_id,
      discount_type: this.item.discount_type,
      discount_value: this.item.discount_value,
      status: this.item.status,
    };
  }

  startEdit(): void {
    this.resetForm();
    this.isEditMode = true;
    this.validationTriggered = false;
  }

  cancelEdit(): void {
    this.resetForm();
    this.isEditMode = false;
    this.validationTriggered = false;
  }

  save(): void {
    this.validationTriggered = true;
    if (
      !this.form.partner_id ||
      !this.form.product_id ||
      this.form.discount_value === '' ||
      this.form.discount_value === null
    ) {
      this.toaster.error('Please fill in all required fields.');
      return;
    }

    const payload = {
      partner_id: this.form.partner_id,
      product_id: Number(this.form.product_id),
      discount_type: Number(this.form.discount_type),
      discount_value: Number(this.form.discount_value),
      status: Number(this.form.status),
    };

    this.addOnService.update(this.id, payload).subscribe({
      next: (result) => {
        this.item = result.data;
        this.isEditMode = false;
        this.validationTriggered = false;
        this.toaster.success('Add-on updated.');
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toaster.error(error?.error?.message || 'Failed to update add-on.');
      },
    });
  }

  partnerName(partnerId: string | null | undefined): string {
    if (!partnerId) {
      return '-';
    }
    const match = this.partners.find((p) => String(p.partner_id) === String(partnerId));
    return match?.name ?? String(partnerId);
  }

  productName(productId: number | string | null | undefined): string {
    if (productId === null || productId === undefined || productId === '') {
      return '-';
    }
    const match = this.products.find((p) => String(p.id) === String(productId));
    return match ? `${match.name} (${match.sku || match.id})` : String(productId);
  }

  discountTypeLabel(type: number): string {
    return type === 1 ? 'Amount' : type === 2 ? 'Percentage' : String(type ?? '-');
  }

  statusLabel(status: number): string {
    return status === 1 ? 'Active' : status === 2 ? 'Inactive' : String(status ?? '-');
  }
}
